import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Typography,
  Button,
  IconButton,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import orgDataService from "services/orgDataService";
import vehiclesService from "services/vehiclesService";
import formsService from "services/formsService";
import { Hub, KeyValue } from "common/models";
import { VehicleListItem } from "vehicles/models";
import { OpenShift } from "forms/models";
import { toast } from "react-toastify";
import DeleteIcon from "@mui/icons-material/Delete";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import AttachFileIcon from "@mui/icons-material/AttachFile";

const steps = ["Select Shifts", "Review Shifts", "Close Shift"];

// Step 1: Select location and date
interface ShiftFilterData {
  locationId: number | null;
  shiftDate: string;
}

// Step 3: Closure form data
interface ClosureFormData {
  startTime: string;
  endTime: string;
  shiftTypeId: number | null;
  driverLocationId: number | null;
  driverId: number | null;
  vehicleLocationId: number | null;
  vehicleId: number | null;
  begMileage: string;
  endMileage: string;
  tripsRan: string;
}

interface ClosureFormErrors {
  startTime?: string;
  endTime?: string;
  shiftTypeId?: string;
  driverLocationId?: string;
  driverId?: string;
  vehicleLocationId?: string;
  vehicleId?: string;
  begMileage?: string;
  endMileage?: string;
  tripsRan?: string;
  document?: string;
}

const ShiftClosing = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);

  // Step 1 state
  const [locations, setLocations] = useState<Hub[]>([]);
  const [filterData, setFilterData] = useState<ShiftFilterData>({
    locationId: null,
    shiftDate: new Date().toISOString().split("T")[0],
  });
  const [isLoadingLocations, setIsLoadingLocations] = useState(true);
  const [isLoadingShifts, setIsLoadingShifts] = useState(false);

  // Step 2 state
  const [shifts, setShifts] = useState<OpenShift[]>([]);
  const [locationName, setLocationName] = useState("");
  const [selectedShift, setSelectedShift] = useState<OpenShift | null>(null);

  // Step 3 state
  const [shiftTypes, setShiftTypes] = useState<KeyValue[]>([]);
  const [drivers, setDrivers] = useState<any[]>([]);
  const [vehicles, setVehicles] = useState<VehicleListItem[]>([]);
  const [isLoadingDrivers, setIsLoadingDrivers] = useState(false);
  const [isLoadingVehicles, setIsLoadingVehicles] = useState(false);
  const [closureForm, setClosureForm] = useState<ClosureFormData>({
    startTime: "",
    endTime: "",
    shiftTypeId: null,
    driverLocationId: null,
    driverId: null,
    vehicleLocationId: null,
    vehicleId: null,
    begMileage: "",
    endMileage: "",
    tripsRan: "",
  });
  const [errors, setErrors] = useState<ClosureFormErrors>({});
  const [documents, setDocuments] = useState<File[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Load locations on mount
  useEffect(() => {
    const loadLocations = async () => {
      try {
        const hubs = await orgDataService.getAllHubs();
        setLocations(hubs);
      } catch {
        toast.error("Failed to load locations");
      } finally {
        setIsLoadingLocations(false);
      }
    };
    loadLocations();
  }, []);

  // Load shift types once
  useEffect(() => {
    const loadShiftTypes = async () => {
      try {
        const types = await orgDataService.getShiftTypes();
        setShiftTypes(types);
      } catch {
        toast.error("Failed to load shift types");
      }
    };
    loadShiftTypes();
  }, []);

  // Track whether dropdown data was pre-loaded by handleSelectShift
  const skipDriverLoad = useRef(false);
  const skipVehicleLoad = useRef(false);

  // Drivers sorted "Last, First" for the dropdown (matches the Overtime Request list)
  const sortedDrivers = useMemo(
    () =>
      [...drivers].sort((a, b) =>
        `${a.lastName ?? a.name ?? ""} ${a.firstName ?? ""}`.localeCompare(
          `${b.lastName ?? b.name ?? ""} ${b.firstName ?? ""}`,
        ),
      ),
    [drivers],
  );

  // Load drivers when driver location changes (skip if pre-loaded)
  useEffect(() => {
    if (skipDriverLoad.current) {
      skipDriverLoad.current = false;
      return;
    }
    if (closureForm.driverLocationId) {
      setIsLoadingDrivers(true);
      orgDataService
        .getDrivers(closureForm.driverLocationId, true)
        .then((data) => setDrivers(data || []))
        .catch(() => {
          toast.error("Failed to load drivers");
          setDrivers([]);
        })
        .finally(() => setIsLoadingDrivers(false));
    } else {
      setDrivers([]);
    }
  }, [closureForm.driverLocationId]);

  // Load vehicles when vehicle location changes (skip if pre-loaded)
  useEffect(() => {
    if (skipVehicleLoad.current) {
      skipVehicleLoad.current = false;
      return;
    }
    if (closureForm.vehicleLocationId) {
      setIsLoadingVehicles(true);
      vehiclesService
        .getVehiclesByHub(closureForm.vehicleLocationId)
        .then((data) => setVehicles(data || []))
        .catch(() => {
          toast.error("Failed to load vehicles");
          setVehicles([]);
        })
        .finally(() => setIsLoadingVehicles(false));
    } else {
      setVehicles([]);
    }
  }, [closureForm.vehicleLocationId]);

  const totalMileage = useMemo(() => {
    const beg = parseInt(closureForm.begMileage) || 0;
    const end = parseInt(closureForm.endMileage) || 0;
    return end - beg;
  }, [closureForm.begMileage, closureForm.endMileage]);

  // Step 1: Get Shifts
  const handleGetShifts = async () => {
    if (!filterData.locationId || !filterData.shiftDate) {
      toast.error("Please select a location and date");
      return;
    }

    setIsLoadingShifts(true);
    try {
      const response = await formsService.getOpenShifts({
        locationId: filterData.locationId,
        shiftDate: filterData.shiftDate,
      });
      setShifts(response.shifts || []);
      setLocationName(response.locationName);
      setActiveStep(1);
    } catch {
      toast.error("Failed to load shifts");
    } finally {
      setIsLoadingShifts(false);
    }
  };

  // Step 2: Select a shift — pre-load dropdown data before showing form
  const [isLoadingShiftData, setIsLoadingShiftData] = useState(false);

  const handleSelectShift = async (shift: OpenShift) => {
    setIsLoadingShiftData(true);
    try {
      // Pre-load drivers and vehicles in parallel so dropdowns are ready
      const [driversData, vehiclesData] = await Promise.all([
        shift.driverLocationID
          ? orgDataService.getDrivers(shift.driverLocationID, true).catch(() => [])
          : Promise.resolve([]),
        shift.vehicleLocationID
          ? vehiclesService.getVehiclesByHub(shift.vehicleLocationID).catch(() => [])
          : Promise.resolve([]),
      ]);

      setDrivers(driversData || []);
      setVehicles(vehiclesData || []);
      skipDriverLoad.current = true;
      skipVehicleLoad.current = true;
      setSelectedShift(shift);
      setClosureForm({
        startTime: shift.timeStart || "",
        endTime: shift.timeEnd || "",
        shiftTypeId: shift.yardShiftTypeID || null,
        driverLocationId: shift.driverLocationID || null,
        driverId: shift.driverID || null,
        vehicleLocationId: shift.vehicleLocationID || null,
        vehicleId: shift.vehicleID || null,
        begMileage: (shift.odometerBeg || 0).toString(),
        endMileage: (shift.odometerEnd || 0).toString(),
        tripsRan: (shift.tripsRan || 0).toString(),
      });
      setDocuments([]);
      setErrors({});
      setActiveStep(2);
    } catch {
      toast.error("Failed to load shift data");
    } finally {
      setIsLoadingShiftData(false);
    }
  };

  // Step 3: Validate closure form
  const validateClosure = (): boolean => {
    const newErrors: ClosureFormErrors = {};

    if (!closureForm.startTime) newErrors.startTime = "Start time is required";
    if (!closureForm.endTime) newErrors.endTime = "End time is required";
    if (!closureForm.shiftTypeId) newErrors.shiftTypeId = "Shift type is required";
    if (!closureForm.driverLocationId) newErrors.driverLocationId = "Driver location is required";
    if (!closureForm.driverId) newErrors.driverId = "Driver is required";
    if (!closureForm.vehicleLocationId) newErrors.vehicleLocationId = "Vehicle location is required";
    if (!closureForm.vehicleId) newErrors.vehicleId = "Van number is required";
    if (!closureForm.begMileage && closureForm.begMileage !== "0")
      newErrors.begMileage = "Begin mileage is required";
    if (!closureForm.endMileage && closureForm.endMileage !== "0")
      newErrors.endMileage = "End mileage is required";
    if (!closureForm.tripsRan && closureForm.tripsRan !== "0")
      newErrors.tripsRan = "Number of trips is required";

    const beg = parseInt(closureForm.begMileage) || 0;
    const end = parseInt(closureForm.endMileage) || 0;
    if (end < beg) newErrors.endMileage = "End mileage must be >= begin mileage";

    if (parseInt(closureForm.begMileage) < 0) newErrors.begMileage = "Must be non-negative";
    if (parseInt(closureForm.endMileage) < 0) newErrors.endMileage = "Must be non-negative";
    if (parseInt(closureForm.tripsRan) < 0) newErrors.tripsRan = "Must be non-negative";

    // Document is optional — PDF is generated with or without an attached image

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Step 4: Submit
  const handleSubmit = async () => {
    if (!validateClosure() || !selectedShift) return;

    setIsSubmitting(true);
    try {
      // Get van unit number from vehicles list
      const selectedVehicle = vehicles.find((v) => v.id === closureForm.vehicleId);
      const vanNumber = selectedVehicle?.unitNumber?.toString() || closureForm.vehicleId?.toString() || "";

      // Submit closure data + document together — backend generates PDF and uploads to Box
      const closureResponse = await formsService.submitShiftClosure(
        {
          yardDailyID: selectedShift.yardDailyID,
          shiftTypeID: closureForm.shiftTypeId!,
          driverID: closureForm.driverId!,
          vehicleID: closureForm.vehicleId!,
          startTime: closureForm.startTime,
          endTime: closureForm.endTime,
          begMileage: parseInt(closureForm.begMileage) || 0,
          endMileage: parseInt(closureForm.endMileage) || 0,
          tripsRan: parseInt(closureForm.tripsRan) || 0,
          locationName: locationName,
          shiftDate: filterData.shiftDate,
          shiftName: selectedShift.shiftName || "",
          vanNumber: vanNumber,
        },
        documents
      );

      if (!closureResponse.success) {
        toast.error(closureResponse.message || "Failed to save shift data. Please try again.");
        return;
      }

      toast.success("Shift closed successfully");
      // Remove closed shift from list and return to shift list
      setShifts((prev) => prev.filter((s) => s.yardDailyID !== selectedShift.yardDailyID));
      setSelectedShift(null);
      setActiveStep(1);
    } catch {
      toast.error("Failed to save shift data. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClosureChange = (field: keyof ClosureFormData, value: any) => {
    if (field === "driverLocationId") {
      setClosureForm((prev) => ({ ...prev, [field]: value, driverId: null }));
    } else if (field === "vehicleLocationId") {
      setClosureForm((prev) => ({ ...prev, [field]: value, vehicleId: null }));
    } else {
      setClosureForm((prev) => ({ ...prev, [field]: value }));
    }
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleFilesSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files;
    if (selected && selected.length > 0) {
      const validTypes = ["image/jpeg", "image/png", "application/pdf"];
      const accepted: File[] = [];
      Array.from(selected).forEach((file) => {
        if (!validTypes.includes(file.type)) {
          toast.error(`${file.name}: please select a JPG, PNG, or PDF file`);
          return;
        }
        if (file.size > 10 * 1024 * 1024) {
          toast.error(`${file.name}: file size must be under 10 MB`);
          return;
        }
        accepted.push(file);
      });
      if (accepted.length > 0) {
        setDocuments((prev) => [...prev, ...accepted]);
        setErrors((prev) => ({ ...prev, document: undefined }));
      }
    }
    // Reset input so the same file can be reselected after removal
    e.target.value = "";
  };

  const handleRemoveDocument = (index: number) => {
    const updated = documents.filter((_, i) => i !== index);
    setDocuments(updated);
    if (updated.length === 0) {
      setErrors((prev) => ({
        ...prev,
        document: "A photo or PDF of the shift sheet is required",
      }));
    }
  };

  if (isLoadingLocations) {
    return (
      <MDBox p={3}>
        <MDTypography>Loading...</MDTypography>
      </MDBox>
    );
  }

  return (
    <MDBox>
      <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <MDBox>
          <MDTypography variant="h5" fontWeight="medium">
            Location Manager Shift Closing
          </MDTypography>
          <MDTypography variant="body2" color="text">
            Close out shifts for a location
          </MDTypography>
        </MDBox>
        <MDButton variant="outlined" color="secondary" onClick={() => navigate("/forms")}>
          Back to Forms
        </MDButton>
      </MDBox>

      <Stepper
        activeStep={activeStep}
        alternativeLabel
        sx={{
          mb: 3,
          background: ({ functions: { linearGradient }, palette: { gradients } }: any) =>
            linearGradient(gradients.dark.main, gradients.dark.state),
          boxShadow: ({ boxShadows: { colored } }: any) => colored.dark,
        }}
      >
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {/* Step 1: Select Location & Date */}
      {activeStep === 0 && (
        <Card>
          <MDBox p={3}>
            <MDTypography variant="h6" fontWeight="medium" mb={2}>
              Shift Request
            </MDTypography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel id="location-label">Location for Shifts *</InputLabel>
                  <Select
                    labelId="location-label"
                    value={filterData.locationId || ""}
                    label="Location for Shifts *"
                    onChange={(e) =>
                      setFilterData({ ...filterData, locationId: e.target.value as number })
                    }
                  >
                    {locations.map((loc) => (
                      <MenuItem key={loc.id} value={loc.id}>
                        {loc.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Date of Shifts *"
                  type="date"
                  value={filterData.shiftDate}
                  onChange={(e) => setFilterData({ ...filterData, shiftDate: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={4} display="flex" alignItems="center">
                <MDButton
                  variant="contained"
                  color="dark"
                  onClick={handleGetShifts}
                  disabled={isLoadingShifts || !filterData.locationId || !filterData.shiftDate}
                >
                  {isLoadingShifts ? "Loading..." : "Get Shifts"}
                </MDButton>
              </Grid>
            </Grid>
          </MDBox>
        </Card>
      )}

      {/* Step 2: Shift List */}
      {activeStep === 1 && (
        <Card>
          <MDBox p={3}>
            <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <MDTypography variant="h6" fontWeight="medium">
                Open Shifts — {locationName} ({filterData.shiftDate})
              </MDTypography>
              <MDButton
                variant="outlined"
                color="secondary"
                size="small"
                onClick={() => setActiveStep(0)}
              >
                Change Filters
              </MDButton>
            </MDBox>

            {shifts.length === 0 ? (
              <MDTypography variant="body2" color="text" py={4} textAlign="center">
                No open shifts found for the selected date and location.
              </MDTypography>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell></TableCell>
                      <TableCell>Shift Name</TableCell>
                      <TableCell>Shift Number</TableCell>
                      <TableCell>Start Time</TableCell>
                      <TableCell>End Time</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {shifts.map((shift) => (
                      <TableRow key={shift.yardDailyID} hover>
                        <TableCell>
                          <MDButton
                            variant="contained"
                            color="dark"
                            size="small"
                            onClick={() => handleSelectShift(shift)}
                            disabled={isLoadingShiftData}
                          >
                            {isLoadingShiftData ? "Loading..." : "Close Shift"}
                          </MDButton>
                        </TableCell>
                        <TableCell>{shift.shiftName}</TableCell>
                        <TableCell>{shift.yardDailyID}</TableCell>
                        <TableCell>{shift.timeStart}</TableCell>
                        <TableCell>{shift.timeEnd}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </MDBox>
        </Card>
      )}

      {/* Step 3: Closure Form */}
      {activeStep === 2 && selectedShift && (
        <Card>
          <MDBox p={3}>
            <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <MDTypography variant="h6" fontWeight="medium">
                Close Shift
              </MDTypography>
              <MDButton
                variant="outlined"
                color="secondary"
                size="small"
                onClick={() => setActiveStep(1)}
              >
                Back to Shift List
              </MDButton>
            </MDBox>

            {/* Shift context — populated from shift data */}
            <MDBox mb={3} p={2} bgcolor="grey.100" borderRadius="lg">
              <Grid container spacing={2}>
                <Grid item xs={6} md={3}>
                  <MDTypography variant="caption" color="text">Location</MDTypography>
                  <MDTypography variant="body2" fontWeight="medium">{locationName}</MDTypography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <MDTypography variant="caption" color="text">Date</MDTypography>
                  <MDTypography variant="body2" fontWeight="medium">{filterData.shiftDate}</MDTypography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <MDTypography variant="caption" color="text">Shift Number</MDTypography>
                  <MDTypography variant="body2" fontWeight="medium">{selectedShift.yardDailyID}</MDTypography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <MDTypography variant="caption" color="text">Shift Name</MDTypography>
                  <MDTypography variant="body2" fontWeight="medium">{selectedShift.shiftName}</MDTypography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <MDTypography variant="caption" color="text">Shift Type</MDTypography>
                  <MDTypography variant="body2" fontWeight="medium">
                    {selectedShift.yardShiftTypeName || "—"}
                  </MDTypography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <MDTypography variant="caption" color="text">Driver Location</MDTypography>
                  <MDTypography variant="body2" fontWeight="medium">
                    {selectedShift.driverLocationName || "—"}
                  </MDTypography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <MDTypography variant="caption" color="text">Driver</MDTypography>
                  <MDTypography variant="body2" fontWeight="medium">
                    {selectedShift.driverID || "—"}
                  </MDTypography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <MDTypography variant="caption" color="text">Vehicle Location</MDTypography>
                  <MDTypography variant="body2" fontWeight="medium">
                    {selectedShift.vehicleLocationName || "—"}
                  </MDTypography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <MDTypography variant="caption" color="text">Van Number</MDTypography>
                  <MDTypography variant="body2" fontWeight="medium">
                    {selectedShift.vehicleID || "—"}
                  </MDTypography>
                </Grid>
              </Grid>
            </MDBox>

            {/* Editable fields */}
            <Grid container spacing={3}>
              {/* Time fields */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Start Time *"
                  type="time"
                  value={closureForm.startTime}
                  onChange={(e) => handleClosureChange("startTime", e.target.value)}
                  error={!!errors.startTime}
                  helperText={errors.startTime || "24-hour format (e.g. 13:00 for 1:00 PM)"}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ step: 60 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="End Time *"
                  type="time"
                  value={closureForm.endTime}
                  onChange={(e) => handleClosureChange("endTime", e.target.value)}
                  error={!!errors.endTime}
                  helperText={errors.endTime || "24-hour format (e.g. 13:00 for 1:00 PM)"}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ step: 60 }}
                />
              </Grid>

              {/* Shift Type */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth error={!!errors.shiftTypeId}>
                  <InputLabel id="shift-type-label">Shift Type *</InputLabel>
                  <Select
                    labelId="shift-type-label"
                    value={closureForm.shiftTypeId || ""}
                    label="Shift Type *"
                    onChange={(e) => handleClosureChange("shiftTypeId", e.target.value)}
                  >
                    {shiftTypes.map((type) => (
                      <MenuItem key={type.id} value={type.id}>
                        {type.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.shiftTypeId && <FormHelperText>{errors.shiftTypeId}</FormHelperText>}
                </FormControl>
              </Grid>

              {/* Driver Location + Driver */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth error={!!errors.driverLocationId}>
                  <InputLabel id="driver-location-label">Driver Location *</InputLabel>
                  <Select
                    labelId="driver-location-label"
                    value={closureForm.driverLocationId || ""}
                    label="Driver Location *"
                    onChange={(e) => handleClosureChange("driverLocationId", e.target.value)}
                  >
                    {locations.map((loc) => (
                      <MenuItem key={loc.id} value={loc.id}>
                        {loc.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.driverLocationId && (
                    <FormHelperText>{errors.driverLocationId}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl
                  fullWidth
                  error={!!errors.driverId}
                  disabled={!closureForm.driverLocationId || isLoadingDrivers}
                >
                  <InputLabel id="driver-label">
                    {isLoadingDrivers ? "Loading drivers..." : "Driver *"}
                  </InputLabel>
                  <Select
                    labelId="driver-label"
                    value={closureForm.driverId || ""}
                    label={isLoadingDrivers ? "Loading drivers..." : "Driver *"}
                    onChange={(e) => handleClosureChange("driverId", e.target.value)}
                  >
                    {sortedDrivers.map((driver: any) => (
                      <MenuItem key={driver.id} value={driver.id}>
                        {driver.lastName
                          ? `${driver.lastName}, ${driver.firstName ?? ""}`
                          : driver.name || driver.id}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.driverId && <FormHelperText>{errors.driverId}</FormHelperText>}
                  {!closureForm.driverLocationId && !errors.driverId && (
                    <FormHelperText>Select a driver location first</FormHelperText>
                  )}
                </FormControl>
              </Grid>

              {/* Vehicle Location + Van Number */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth error={!!errors.vehicleLocationId}>
                  <InputLabel id="vehicle-location-label">Vehicle Location *</InputLabel>
                  <Select
                    labelId="vehicle-location-label"
                    value={closureForm.vehicleLocationId || ""}
                    label="Vehicle Location *"
                    onChange={(e) => handleClosureChange("vehicleLocationId", e.target.value)}
                  >
                    {locations.map((loc) => (
                      <MenuItem key={loc.id} value={loc.id}>
                        {loc.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.vehicleLocationId && (
                    <FormHelperText>{errors.vehicleLocationId}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl
                  fullWidth
                  error={!!errors.vehicleId}
                  disabled={!closureForm.vehicleLocationId || isLoadingVehicles}
                >
                  <InputLabel id="vehicle-label">
                    {isLoadingVehicles ? "Loading vehicles..." : "Van Number *"}
                  </InputLabel>
                  <Select
                    labelId="vehicle-label"
                    value={closureForm.vehicleId || ""}
                    label={isLoadingVehicles ? "Loading vehicles..." : "Van Number *"}
                    onChange={(e) => handleClosureChange("vehicleId", e.target.value)}
                  >
                    {vehicles.map((v) => (
                      <MenuItem key={v.id} value={v.id}>
                        {v.unitNumber}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.vehicleId && <FormHelperText>{errors.vehicleId}</FormHelperText>}
                  {!closureForm.vehicleLocationId && !errors.vehicleId && (
                    <FormHelperText>Select a vehicle location first</FormHelperText>
                  )}
                </FormControl>
              </Grid>

              {/* Trips & Mileage */}
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Number of Trips *"
                  type="number"
                  value={closureForm.tripsRan}
                  onChange={(e) => handleClosureChange("tripsRan", e.target.value)}
                  error={!!errors.tripsRan}
                  helperText={errors.tripsRan}
                  inputProps={{ min: 0 }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Begin Mileage *"
                  type="number"
                  value={closureForm.begMileage}
                  onChange={(e) => handleClosureChange("begMileage", e.target.value)}
                  error={!!errors.begMileage}
                  helperText={errors.begMileage}
                  inputProps={{ min: 0 }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="End Mileage *"
                  type="number"
                  value={closureForm.endMileage}
                  onChange={(e) => handleClosureChange("endMileage", e.target.value)}
                  error={!!errors.endMileage}
                  helperText={errors.endMileage}
                  inputProps={{ min: 0 }}
                />
              </Grid>

              {/* Total Mileage */}
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Total Mileage"
                  value={totalMileage}
                  disabled
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>

            {/* Document Upload */}
            <MDBox mt={3}>
              <MDTypography variant="body2" color="text" mb={1}>
                Below are two choices. Either you can take a picture of the individual shift sheet or
                you can attach a PDF. One of the two needs to be completed for proper shift closing.
              </MDTypography>
              <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                <input
                  ref={cameraInputRef}
                  type="file"
                  accept="image/jpeg,image/png"
                  capture="environment"
                  style={{ display: "none" }}
                  onChange={handleFilesSelected}
                />
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,application/pdf"
                  multiple
                  style={{ display: "none" }}
                  onChange={handleFilesSelected}
                />
                <MDButton
                  variant="contained"
                  color="dark"
                  startIcon={<PhotoCameraIcon />}
                  onClick={() => cameraInputRef.current?.click()}
                >
                  Take Photo
                </MDButton>
                <MDButton
                  variant="outlined"
                  color="dark"
                  startIcon={<AttachFileIcon />}
                  onClick={() => fileInputRef.current?.click()}
                >
                  Attach Photos or PDFs
                </MDButton>
              </Box>
              {documents.length > 0 && (
                <Box display="flex" flexDirection="column" gap={1} mt={2}>
                  {documents.map((file, index) => (
                    <Box key={`${file.name}-${index}`} display="flex" alignItems="center" gap={1}>
                      <Typography variant="body2">{file.name}</Typography>
                      <IconButton size="small" onClick={() => handleRemoveDocument(index)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              )}
              {errors.document && (
                <FormHelperText error>{errors.document}</FormHelperText>
              )}
            </MDBox>

            {/* Submit */}
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 4, gap: 2 }}>
              <MDButton variant="outlined" color="secondary" onClick={() => setActiveStep(1)}>
                Cancel
              </MDButton>
              <MDButton
                variant="contained"
                color="dark"
                onClick={handleSubmit}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Sending..." : "Send"}
              </MDButton>
            </Box>
          </MDBox>
        </Card>
      )}
    </MDBox>
  );
};

export default ShiftClosing;
